'use client';

import { useCallback, useEffect, useState } from 'react';

/**
 * Merkt einen Ja/Nein-Wunsch des Nutzers in `localStorage`.
 *
 * Der erste Render liefert immer `null`, unabhängig vom Speicherinhalt: auf
 * dem Server gibt es kein `localStorage`, und ein abweichender erster
 * Client-Render würde die Hydration reißen. Gelesen wird deshalb erst im
 * Effekt nach dem Mount.
 *
 * `null` bedeutet für Aufrufer „kein Wunsch" — sowohl „noch nicht gelesen"
 * als auch „nichts gespeichert". Beide Fälle behandelt der Aufrufer gleich.
 *
 * Jeder Zugriff ist gekapselt: In privaten Browser-Modi wirft `localStorage`,
 * und das darf die Seite nicht mitnehmen.
 */
export function useRememberedFlag(storageKey: string): {
  remembered: boolean | null;
  remember: (value: boolean) => void;
} {
  const [remembered, setRemembered] = useState<boolean | null>(null);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === 'true') setRemembered(true);
      else if (raw === 'false') setRemembered(false);
      else setRemembered(null);
    } catch {
      setRemembered(null);
    }
  }, [storageKey]);

  const remember = useCallback(
    (value: boolean) => {
      setRemembered(value);
      try {
        window.localStorage.setItem(storageKey, value ? 'true' : 'false');
      } catch {
        // Kein Speicher verfügbar — die Wahl gilt dann nur für diese Sitzung.
      }
    },
    [storageKey],
  );

  return { remembered, remember };
}
